import React, { useState, useEffect } from 'react';
import { storage } from '../utils/storage';
import { getTopPlayers, getLastMatch } from '../utils/calculations';
import { Player, Match } from '../types';
import { toTurkishUpperCase } from '../utils/turkishUtils';
import { ensureHttps } from '../utils/urlUtils';
import { getPlayerDisplayName } from '../utils/matchStats';
import PlayerProfileModal from '../components/PlayerProfileModal';
import TeamLogo from '../components/TeamLogo';
import GoalIcon from '../components/GoalIcon';
import AssistIcon from '../components/AssistIcon';
import TopPlayerIcon from '../components/TopPlayerIcon';
import { MatchCountdown } from '../components/MatchCountdown';
import './HomePage.css';

const HomePage: React.FC = () => {
  const [homeImage, setHomeImage] = useState<string>('/icons/homepage-image.png');
  const [matchdayImage, setMatchdayImage] = useState<string>('');
  const [currentSeason, setCurrentSeason] = useState<string>('');
  const [topPlayers, setTopPlayers] = useState<any>({});
  const [lastMatch, setLastMatch] = useState<Match | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [showMatchDetail, setShowMatchDetail] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [showMatchdaySplash, setShowMatchdaySplash] = useState(false);
  const [links, setLinks] = useState<{ 
    location?: { name: string; url: string; icon?: string }; 
    googleDrive?: { name: string; url: string; icon?: string }; 
    instagram?: { name: string; url: string; icon?: string }; 
    whatsapp?: { name: string; url: string; icon?: string }; 
    other?: Array<{ id: string; name: string; url: string; icon?: string }> 
  }>({});
  const isAdmin = storage.isAdminMode();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Matchday splash screen kontrolü (Pazartesi 00:00 - Salı 06:00 arası)
    if (!matchdayImage) return;
    
    const checkMatchdaySplash = () => {
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Pazar, 1 = Pazartesi, 2 = Salı
      const hours = now.getHours();
      
      // Pazartesi 00:00 - Salı 06:00 arası mı?
      const isMatchdayTime = (dayOfWeek === 1) || (dayOfWeek === 2 && hours < 6);
      
      if (isMatchdayTime) {
        // Bu hafta gösterildi mi kontrol et
        const lastShown = localStorage.getItem('matchday_splash_last_shown');
        const thisWeek = `${now.getFullYear()}-W${Math.ceil((now.getDate() + new Date(now.getFullYear(), 0, 1).getDay()) / 7)}`;
        
        if (lastShown !== thisWeek) {
          setShowMatchdaySplash(true);
          localStorage.setItem('matchday_splash_last_shown', thisWeek);
        }
      }
    };
    
    checkMatchdaySplash();
  }, [matchdayImage]);

  const loadData = async () => {
    const settings = await storage.getAdminSettings();
    // Ana sayfa görseli - önce settings'ten kontrol et
    if (settings.homePageImage) {
      setHomeImage(settings.homePageImage);
    } else {
      // Varsayılan görsel - icons klasöründen yükle (build klasöründe icons/homepage-image.png)
      // PNG formatında olduğu için direkt .png'i kullan
      setHomeImage('/icons/homepage-image.png');
    }
    
    // Matchday görseli yükle
    setMatchdayImage(settings.matchdayImage || '');
    
    // Linkleri yükle
    const loadedLinks: any = settings.links || {};
    setLinks(loadedLinks);
    
    const seasons = await storage.getSeasons();
    const activeSeason = seasons.find(s => s.isActive);
    const seasonName = activeSeason?.name || seasons[0]?.name || '';
    setCurrentSeason(seasonName ? `LONCA ${toTurkishUpperCase(seasonName)}` : '');
    
    // Oyuncuları yükle (cache otomatik yönetiliyor)
    const allPlayers = await storage.getPlayers();
    setPlayers(allPlayers);
    
    const topPlayersData = await getTopPlayers();
    setTopPlayers(topPlayersData);
    const lastMatchData = await getLastMatch();
    setLastMatch(lastMatchData);
  };

  // Matchday görseli yükleme (Pazartesi 00:00 - Salı 06:00 arası ilk girişte gösterilecek)
  const handleMatchdayImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    
    try {
      // Hosting'e yükle
      const imageUrl = await storage.uploadImage(file, `matchday/${Date.now()}-${file.name}`);
      
      const settings = await storage.getAdminSettings();
      settings.matchdayImage = imageUrl;
      
      // Eğer Salı 06:00'dan sonra ise, bu görseli geçmiş görseller listesine ekle
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Pazar, 1 = Pazartesi, 2 = Salı
      const hours = now.getHours();
      const isAfterTuesday6AM = dayOfWeek === 2 && hours >= 6;
      
      if (isAfterTuesday6AM) {
        if (!settings.matchdayImages) {
          settings.matchdayImages = [];
        }
        const newImageEntry = {
          id: `matchday-${Date.now()}`,
          url: imageUrl,
          date: new Date().toISOString()
        };
        settings.matchdayImages.push(newImageEntry);
      }
      
      await storage.saveAdminSettings(settings);
      setMatchdayImage(imageUrl);
      alert('Matchday görseli başarıyla yüklendi! (Pazartesi 00:00 - Salı 06:00 arası uygulamaya ilk girildiğinde gösterilecek)' + 
            (isAfterTuesday6AM ? ' Görsel geçmiş listesine de eklendi.' : ''));
    } catch (error) {
      console.error('Görsel yükleme hatası:', error);
      alert('Görsel yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    }
    
    e.target.value = '';
  };

  const getPlayerName = (playerId: string): string => {
    return toTurkishUpperCase(getPlayerDisplayName(players, playerId));
  };

  const handlePlayerClick = (playerId: string) => {
    const player = players.find(p => p.id === playerId);
    if (player) {
      setSelectedPlayer(player);
    }
  };

  // Profil fotoğrafı gösterimini basitleştir (puan durumu sayfasındaki gibi)
  const renderPlayerImage = (player: Player) => {
    return (
      <div className="top-player-image">
        {player.profileImage ? (
          <img 
            src={ensureHttps(player.profileImage)} 
            alt={player.name}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              const parent = target.parentElement;
              if (!parent) return;
              
              const existingPlaceholder = parent.querySelector('.top-player-placeholder');
              if (existingPlaceholder) return;
              
              target.style.display = 'none';
              
              if (player.image && player.image !== player.profileImage) {
                const altImg = document.createElement('img');
                altImg.src = player.image;
                altImg.alt = player.name;
                altImg.className = target.className;
                altImg.onerror = () => {
                  altImg.style.display = 'none';
                  const placeholder = document.createElement('div');
                  placeholder.className = 'top-player-placeholder';
                  placeholder.textContent = player.name[0];
                  parent.appendChild(placeholder);
                };
                parent.appendChild(altImg);
              } else {
                const placeholder = document.createElement('div');
                placeholder.className = 'top-player-placeholder';
                placeholder.textContent = player.name[0];
                parent.appendChild(placeholder);
              }
            }}
            loading="lazy"
            decoding="async"
          />
        ) : player.image ? (
          <img 
            src={ensureHttps(player.image)} 
            alt={player.name}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              const parent = target.parentElement;
              if (!parent) return;
              
              target.style.display = 'none';
              
              const existingPlaceholder = parent.querySelector('.top-player-placeholder');
              if (!existingPlaceholder) {
                const placeholder = document.createElement('div');
                placeholder.className = 'top-player-placeholder';
                placeholder.textContent = player.name[0];
                parent.appendChild(placeholder);
              }
            }}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="top-player-placeholder">{player.name[0]}</div>
        )}
      </div>
    );
  };

  return (
    <div className="home-page">
      {/* Matchday Splash Screen (Pazartesi 00:00 - Salı 06:00 arası ilk girişte) */}
      {showMatchdaySplash && matchdayImage && (
        <div 
          className="matchday-splash-overlay"
          onClick={() => setShowMatchdaySplash(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <img 
            src={ensureHttps(matchdayImage)} 
            alt="Matchday" 
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
      )}

      {isAdmin && (
        <div className="admin-image-upload">
          <div className="admin-matchday-upload">
            <label>
              MATCHDAY Görseli Yükle
              <input
                type="file"
                accept="image/*"
                onChange={handleMatchdayImageUpload}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </div>
      )}
      
      {homeImage && (
        <div className="home-image-section">
          <img 
            src={ensureHttps(homeImage)} 
            alt="Ana Sayfa" 
            className="home-image" 
            onError={(e) => {
              // Eğer .png yüklenemezse .jpg'i dene, sonra images klasöründen dene
              const target = e.target as HTMLImageElement;
              console.log('[HomePage] Ana sayfa görseli yüklenemedi:', target.src);
              
              if (target.src.includes('homepage-image.png')) {
                // Önce icons klasöründen .jpg'i dene
                if (target.src.includes('/icons/')) {
                  target.src = '/icons/homepage-image.jpg';
                } else {
                  // images klasöründen dene
                  target.src = '/images/homepage-image.png';
                }
              } else if (target.src.includes('homepage-image.jpg')) {
                // .jpg de yüklenemezse, images klasöründen .png'i dene
                target.src = '/images/homepage-image.png';
              } else {
                // Tüm formatlar yüklenemezse gizle
                console.warn('[HomePage] Ana sayfa görseli hiçbir yoldan yüklenemedi');
                target.style.display = 'none';
              }
            }}
            onLoad={() => {
              console.log('[HomePage] Ana sayfa görseli başarıyla yüklendi:', homeImage);
            }}
          />
        </div>
      )}

      <div className="current-season-header">
        <h1 className="page-title">{currentSeason}</h1>
        <MatchCountdown />
      </div>

      <div className="top-players-section">
        <div className="top-players-list">
          {topPlayers.leader && (
            <div className="top-player-row" onClick={() => handlePlayerClick(topPlayers.leader.player.id)}>
              <div className="top-player-icon">
                <TopPlayerIcon type="leader" size={24} />
              </div>
              <span className="top-player-label">Lider</span>
              {renderPlayerImage(topPlayers.leader.player)}
              <span className="top-player-name">{toTurkishUpperCase(topPlayers.leader.player.name)}</span>
              <span className="top-player-value">{topPlayers.leader.value} Puan</span>
            </div>
          )}
          {topPlayers.topGoalContribution && (
            <div className="top-player-row" onClick={() => handlePlayerClick(topPlayers.topGoalContribution.player.id)}>
              <div className="top-player-icon">
                <TopPlayerIcon type="topGoalContribution" size={24} />
              </div>
              <span className="top-player-label">Gol Katkısı</span>
              {renderPlayerImage(topPlayers.topGoalContribution.player)}
              <span className="top-player-name">{toTurkishUpperCase(topPlayers.topGoalContribution.player.name)}</span>
              <span className="top-player-value">{topPlayers.topGoalContribution.value} Katkı</span>
            </div>
          )}
          {topPlayers.topScorer && (
            <div className="top-player-row" onClick={() => handlePlayerClick(topPlayers.topScorer.player.id)}>
              <div className="top-player-icon">
                <TopPlayerIcon type="topScorer" size={24} />
              </div>
              <span className="top-player-label">Gol Kralı</span>
              {renderPlayerImage(topPlayers.topScorer.player)}
              <span className="top-player-name">{toTurkishUpperCase(topPlayers.topScorer.player.name)}</span>
              <span className="top-player-value">{topPlayers.topScorer.value} Gol</span>
            </div>
          )}
          {topPlayers.topAssister && (
            <div className="top-player-row" onClick={() => handlePlayerClick(topPlayers.topAssister.player.id)}>
              <div className="top-player-icon">
                <TopPlayerIcon type="topAssister" size={24} />
              </div>
              <span className="top-player-label">Asist Kralı</span>
              {renderPlayerImage(topPlayers.topAssister.player)}
              <span className="top-player-name">{toTurkishUpperCase(topPlayers.topAssister.player.name)}</span>
              <span className="top-player-value">{topPlayers.topAssister.value} Asist</span>
            </div>
          )}
          {topPlayers.mostLoyal && (
            <div className="top-player-row" onClick={() => handlePlayerClick(topPlayers.mostLoyal.player.id)}>
              <div className="top-player-icon">
                <TopPlayerIcon type="mostLoyal" size={24} />
              </div>
              <span className="top-player-label">En Sadık</span>
              {renderPlayerImage(topPlayers.mostLoyal.player)}
              <span className="top-player-name">{toTurkishUpperCase(topPlayers.mostLoyal.player.name)}</span>
              <span className="top-player-value">{topPlayers.mostLoyal.value} Maç</span>
            </div>
          )}
          {topPlayers.leastLoyal && (
            <div className="top-player-row" onClick={() => handlePlayerClick(topPlayers.leastLoyal.player.id)}>
              <div className="top-player-icon">
                <TopPlayerIcon type="leastLoyal" size={24} />
              </div>
              <span className="top-player-label">Konserci</span>
              {renderPlayerImage(topPlayers.leastLoyal.player)}
              <span className="top-player-name">{toTurkishUpperCase(topPlayers.leastLoyal.player.name)}</span>
              <span className="top-player-value">{topPlayers.leastLoyal.value} Maç</span>
            </div>
          )}
        </div>
      </div>

      {lastMatch && (
        <div className="last-match-section">
          <h2>Son Maç</h2>
          <div 
            className="last-match-card clickable"
            onClick={() => setShowMatchDetail(true)}
          >
            <div className="match-date">
              {new Date(lastMatch.date).toLocaleDateString('tr-TR')}
            </div>
            <div className="match-score">
              <span>
                <TeamLogo team="black" size={48} />
                <span className="team-name">SİYAH</span> {lastMatch.blackScore} - {lastMatch.whiteScore} <span className="team-name">BEYAZ</span>
                <TeamLogo team="white" size={48} />
              </span>
            </div>
          </div>
        </div>
      )}

      {showMatchDetail && lastMatch && (
        <MatchDetailModal
          match={lastMatch}
          onClose={() => setShowMatchDetail(false)}
          onPlayerClick={handlePlayerClick}
          getPlayerName={getPlayerName}
        />
      )}

      {/* Linkler Bölümü */}
      <div className="two-column-section">
        <div className="links-column">
          <h2 className="section-title">Linkler</h2>
          <div className="links-list">
            {links.location && links.location.url && (
              <a 
                href={links.location.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="link-item"
              >
                {links.location.icon && links.location.icon.trim() !== '' ? (
                  <img 
                    src={ensureHttps(links.location.icon)} 
                    alt={links.location.name} 
                    className="link-icon"
                    onError={(e) => {
                      console.error('[HomePage] Location ikonu yüklenemedi:', links.location?.icon);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.link-icon-placeholder')) {
                        const placeholder = document.createElement('span');
                        placeholder.className = 'link-icon-placeholder';
                        placeholder.textContent = '📍';
                        parent.insertBefore(placeholder, target);
                      }
                    }}
                    onLoad={() => {
                      console.log('[HomePage] Location ikonu başarıyla yüklendi:', links.location?.icon);
                    }}
                  />
                ) : (
                  <span className="link-icon-placeholder">📍</span>
                )}
                <span className="link-name">{links.location.name || 'Halı Saha'}</span>
              </a>
            )}
            {links.googleDrive && links.googleDrive.url && (
              <a 
                href={links.googleDrive.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="link-item"
              >
                {links.googleDrive.icon && links.googleDrive.icon.trim() !== '' ? (
                  <img 
                    src={ensureHttps(links.googleDrive.icon)} 
                    alt={links.googleDrive.name} 
                    className="link-icon"
                    onError={(e) => {
                      console.error('[HomePage] GoogleDrive ikonu yüklenemedi:', links.googleDrive?.icon);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.link-icon-placeholder')) {
                        const placeholder = document.createElement('span');
                        placeholder.className = 'link-icon-placeholder';
                        placeholder.textContent = '📁';
                        parent.insertBefore(placeholder, target);
                      }
                    }}
                    onLoad={() => {
                      console.log('[HomePage] GoogleDrive ikonu başarıyla yüklendi:', links.googleDrive?.icon);
                    }}
                  />
                ) : (
                  <span className="link-icon-placeholder">📁</span>
                )}
                <span className="link-name">{links.googleDrive.name || 'Google Drive'}</span>
              </a>
            )}
            {links.instagram && links.instagram.url && (
              <a 
                href={links.instagram.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="link-item"
              >
                {links.instagram.icon && links.instagram.icon.trim() !== '' ? (
                  <img 
                    src={ensureHttps(links.instagram.icon)} 
                    alt={links.instagram.name} 
                    className="link-icon"
                    onError={(e) => {
                      console.error('[HomePage] Instagram ikonu yüklenemedi:', links.instagram?.icon);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.link-icon-placeholder')) {
                        const placeholder = document.createElement('span');
                        placeholder.className = 'link-icon-placeholder';
                        placeholder.textContent = '📷';
                        parent.insertBefore(placeholder, target);
                      }
                    }}
                    onLoad={() => {
                      console.log('[HomePage] Instagram ikonu başarıyla yüklendi:', links.instagram?.icon);
                    }}
                  />
                ) : (
                  <span className="link-icon-placeholder">📷</span>
                )}
                <span className="link-name">{links.instagram.name || 'Instagram'}</span>
              </a>
            )}
            {links.whatsapp && links.whatsapp.url && (
              <a 
                href={links.whatsapp.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="link-item"
              >
                {links.whatsapp.icon && links.whatsapp.icon.trim() !== '' ? (
                  <img 
                    src={ensureHttps(links.whatsapp.icon)} 
                    alt={links.whatsapp.name} 
                    className="link-icon"
                    onError={(e) => {
                      console.error('[HomePage] WhatsApp ikonu yüklenemedi:', links.whatsapp?.icon);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.link-icon-placeholder')) {
                        const placeholder = document.createElement('span');
                        placeholder.className = 'link-icon-placeholder';
                        placeholder.textContent = '💬';
                        parent.insertBefore(placeholder, target);
                      }
                    }}
                    onLoad={() => {
                      console.log('[HomePage] WhatsApp ikonu başarıyla yüklendi:', links.whatsapp?.icon);
                    }}
                  />
                ) : (
                  <span className="link-icon-placeholder">💬</span>
                )}
                <span className="link-name">{links.whatsapp.name || 'WhatsApp'}</span>
              </a>
            )}
            {links.other && links.other.map((link) => (
              <a 
                key={link.id}
                href={link.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="link-item"
              >
                {link.icon && link.icon.trim() !== '' ? (
                  <img 
                    src={ensureHttps(link.icon)} 
                    alt={link.name} 
                    className="link-icon"
                    onError={(e) => {
                      console.error('[HomePage] Link ikonu yüklenemedi:', link.icon, 'Link:', link.name);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      // Placeholder'ı sadece yoksa ekle
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.link-icon-placeholder')) {
                        const placeholder = document.createElement('span');
                        placeholder.className = 'link-icon-placeholder';
                        placeholder.textContent = '🔗';
                        parent.insertBefore(placeholder, target);
                      }
                    }}
                    onLoad={() => {
                      console.log('[HomePage] Link ikonu başarıyla yüklendi:', link.icon, 'Link:', link.name);
                    }}
                  />
                ) : (
                  <span className="link-icon-placeholder">🔗</span>
                )}
                <span className="link-name">{link.name}</span>
              </a>
            ))}
          </div>
          {isAdmin && (
            <button 
              className="edit-links-button"
              onClick={() => setShowLinksModal(true)}
            >
              Linkleri Düzenle
            </button>
          )}
        </div>
      </div>

      {/* Linkleri Düzenle Modal */}
      {showLinksModal && (
        <div className="modal-overlay" onClick={() => setShowLinksModal(false)}>
          <div className="links-edit-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={() => setShowLinksModal(false)}>×</button>
            <h2>Linkleri Düzenle</h2>
            <div className="links-edit-form">
              <div className="link-edit-item">
                <div className="link-edit-header">
                  <h3>Diğer Linkler</h3>
                </div>
                {(links.other || []).map((link) => (
                  <div key={link.id} className="other-link-edit-item">
                    <div className="other-link-header">
                      <button
                        className="delete-link-button"
                        onClick={() => {
                          const newOther = (links.other || []).filter(l => l.id !== link.id);
                          setLinks({ ...links, other: newOther.length > 0 ? newOther : undefined });
                        }}
                      >
                        Sil
                      </button>
                    </div>
                    <div className="form-group">
                      <label>İsim:</label>
                      <input
                        type="text"
                        value={link.name}
                        onChange={(e) => {
                          const newOther = (links.other || []).map(l => 
                            l.id === link.id ? { ...l, name: e.target.value } : l
                          );
                          setLinks({ ...links, other: newOther });
                        }}
                        placeholder="Link Adı"
                      />
                    </div>
                    <div className="form-group">
                      <label>URL:</label>
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => {
                          const newOther = (links.other || []).map(l => 
                            l.id === link.id ? { ...l, url: e.target.value } : l
                          );
                          setLinks({ ...links, other: newOther });
                        }}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="form-group">
                      <label>İkon (Opsiyonel):</label>
                      <div className="icon-upload-section">
                        {link.icon && (
                          <img src={ensureHttps(link.icon)} alt="İkon" className="icon-preview" />
                        )}
                        <label className="icon-upload-button">
                          {link.icon ? 'İkonu Değiştir' : 'İkon Yükle'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              if (!e.target.files || !e.target.files[0]) return;
                              const file = e.target.files[0];
                              try {
                                console.log('[HomePage] İkon yükleniyor...', file.name);
                                const iconUrl = await storage.uploadImage(file, `links/icons/other-${link.id}-${Date.now()}-${file.name}`);
                                console.log('[HomePage] İkon yüklendi, URL:', iconUrl);
                                const newOther = (links.other || []).map(l => 
                                  l.id === link.id ? { ...l, icon: iconUrl } : l
                                );
                                setLinks({ ...links, other: newOther });
                                alert('İkon başarıyla yüklendi!');
                              } catch (error) {
                                console.error('İkon yükleme hatası:', error);
                                alert('İkon yüklenirken bir hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
                              }
                              e.target.value = '';
                            }}
                            style={{ display: 'none' }}
                          />
                        </label>
                        {link.icon && (
                          <button
                            className="remove-icon-button"
                            onClick={() => {
                              const newOther = (links.other || []).map(l => 
                                l.id === link.id ? { ...l, icon: undefined } : l
                              );
                              setLinks({ ...links, other: newOther });
                            }}
                          >
                            İkonu Kaldır
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  className="add-other-link-button"
                  onClick={() => {
                    setLinks({
                      ...links,
                      other: [...(links.other || []), { 
                        id: `other-${Date.now()}`, 
                        name: '', 
                        url: '',
                        icon: undefined
                      }]
                    });
                  }}
                >
                  + Yeni Link Ekle
                </button>
              </div>

              <div className="modal-actions">
                <button
                  className="save-button"
                  onClick={async () => {
                    const settings = await storage.getAdminSettings();
                    settings.links = links;
                    await storage.saveAdminSettings(settings);
                    alert('Linkler başarıyla kaydedildi!');
                    setShowLinksModal(false);
                    loadData();
                  }}
                >
                  Kaydet
                </button>
                <button
                  className="cancel-button"
                  onClick={() => {
                    loadData();
                    setShowLinksModal(false);
                  }}
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedPlayer && (
        <PlayerProfileModal
          player={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          isCompact={false}
        />
      )}
    </div>
  );
};

interface MatchDetailModalProps {
  match: Match;
  onClose: () => void;
  onPlayerClick: (playerId: string) => void;
  getPlayerName: (playerId: string) => string;
}

const MatchDetailModal: React.FC<MatchDetailModalProps> = ({ match, onClose, onPlayerClick, getPlayerName }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="match-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        <h2>Maç Detayları</h2>
        
        <div className="match-info">
          <div className="match-date-large">
            {new Date(match.date).toLocaleDateString('tr-TR')}
          </div>
          <div className="match-score-large">
            <TeamLogo team="black" size={56} />
            <span className="team-name">SİYAH</span> {match.blackScore} - {match.whiteScore} <span className="team-name">BEYAZ</span>
            <TeamLogo team="white" size={56} />
          </div>
        </div>

        <div className="match-events-detail">
          <h3>Gol ve Asistler:</h3>
          {[...match.events].sort((a, b) => a.minute - b.minute).map(event => (
            <div key={event.id} className={`event-detail-item event-detail-item-${event.team}`}>
              <span className="minute">{event.minute}'</span>
              <span className="team-badge">
                <TeamLogo team={event.team} size={28} />
              </span>
              <GoalIcon size={20} team={event.team} />
              {event.guestScorerName ? (
                <span>{toTurkishUpperCase(event.guestScorerName)}{event.ownGoal && ' (KK)'}</span>
              ) : (
                <span 
                  className={`player-name-clickable player-name-${event.team}`}
                  onClick={() => event.scorerId && onPlayerClick(event.scorerId)}
                >
                  {event.scorerId ? getPlayerName(event.scorerId) : ''}{event.ownGoal && ' (KK)'}
                </span>
              )}
              {(event.guestAssistName || event.assistId) && (
                <>
                  <AssistIcon size={20} team={event.team} />
                  {event.guestAssistName ? (
                    <span>{toTurkishUpperCase(event.guestAssistName)}</span>
                  ) : (
                    <span 
                      className={`player-name-clickable player-name-${event.team}`}
                      onClick={() => event.assistId && onPlayerClick(event.assistId!)}
                    >
                      {event.assistId ? getPlayerName(event.assistId) : ''}
                    </span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {(match.blackCard && match.blackCardImage) || (match.mvdp && match.mvdpImage) ? (
          (match.blackCard && match.blackCardImage && match.mvdp && match.mvdpImage) ? (
            <div className="match-awards-container">
              <div className="match-award-single">
                <div className="award-header">
                  <strong>Siyah Kart: </strong>
                  <span 
                    className="player-name-clickable" 
                    onClick={() => onPlayerClick(match.blackCard!)}
                  >
                    {getPlayerName(match.blackCard)}
                  </span>
                </div>
                <div className="award-image-container-square">
                  <img src={ensureHttps(match.blackCardImage)} alt="Siyah Kart" className="award-image-square" />
                </div>
              </div>
              <div className="match-award-single">
                <div className="award-header">
                  <strong>MVDP: </strong>
                  <span 
                    className="player-name-clickable" 
                    onClick={() => onPlayerClick(match.mvdp!)}
                  >
                    {getPlayerName(match.mvdp)}
                  </span>
                </div>
                <div className="award-image-container-square">
                  <img src={ensureHttps(match.mvdpImage)} alt="MVDP Kart" className="award-image-square" />
                </div>
              </div>
            </div>
          ) : (
            <>
              {match.blackCard && match.blackCardImage && (
                <div className="match-award">
                  <div className="award-header">
                    <strong>Siyah Kart: </strong>
                    <span 
                      className="player-name-clickable" 
                      onClick={() => onPlayerClick(match.blackCard!)}
                    >
                      {getPlayerName(match.blackCard)}
                    </span>
                  </div>
                  <div className="award-image-container">
                    <img src={ensureHttps(match.blackCardImage)} alt="Siyah Kart" className="award-image" />
                  </div>
                </div>
              )}
              {match.mvdp && match.mvdpImage && (
                <div className="match-award">
                  <div className="award-header">
                    <strong>MVDP Kart: </strong>
                    <span 
                      className="player-name-clickable" 
                      onClick={() => onPlayerClick(match.mvdp!)}
                    >
                      {getPlayerName(match.mvdp)}
                    </span>
                  </div>
                  <div className="award-image-container">
                    <img src={ensureHttps(match.mvdpImage)} alt="MVDP Kart" className="award-image" />
                  </div>
                </div>
              )}
            </>
          )
        ) : null}

        {match.matchImage && (
          <div className="match-image-container">
            <img src={ensureHttps(match.matchImage)} alt="Maç Kadroları" className="match-image-vertical" />
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;



